import { message, Space, SpinProps, Table, Tabs, Typography } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { PricingTableImport } from 'components/molecules/PricingTableImport';
import { ImportHeader } from 'components/organisms/ImportHeader';
import { Stack } from 'components/UI';
import { RateLimiter } from 'limiter';
import { PricingRules, ProductCatalogItem } from 'models/ProductCatalog';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useLazyGetProductCatalogItemsQuery, useUpdateProductCatalogItemMutation } from 'redux/services/julia/juliaApi';
import { setErrorItems, setImportItems, setSuccessItems } from 'redux/slices/importSlice';
import { useAppSelector } from 'redux/store';

const { TabPane } = Tabs;
const { Text } = Typography;

export const ImportPricingRules: React.FC = () => {
  const { id: entityId } = useParams();
  const { pathname } = useLocation();
  const isBusinessEdit = pathname.includes('business/');

  const { importPricingRules, errorItems } = useAppSelector((state) => state.import);

  const dispatch = useDispatch();

  const [activeTab, _setActiveTab] = useState('success');
  const [_successItems, _setSuccessItems] = useState<ProductCatalogItem[]>([]);
  const [_errorItems, _setErrorItems] = useState<{ productNumber: string; errors: string[] }[]>(errorItems);
  const isImporting = useRef(false);
  const [getCatalogItems] = useLazyGetProductCatalogItemsQuery();

  const importTotal = Object.values(importPricingRules).reduce((acc, item) => acc + item.length, 0);

  const [updatePricingRules] = useUpdateProductCatalogItemMutation();

  /* ******************** Functions ******************** */
  const handleNavigation = (activeKey: string): void => {
    _setActiveTab(activeKey);
  };

  /* ****************** Variables ****************** */
  const importLoadingConfig: SpinProps = {
    indicator: (
      <Space style={{ width: 400, marginLeft: -200 }} size="small">
        <BlockLoader direction="loader loader--slideUp" />
        <Text>{`Importing Rules...`}</Text>
      </Space>
    ),
    spinning: importTotal !== _successItems.length + _errorItems.length,
    wrapperClassName: importTotal !== _successItems.length + _errorItems.length ? 'list-loading' : ''
  };

  const importCatalogItems = useCallback(async (): Promise<void> => {
    if (entityId) {
      const limiter = new RateLimiter({ tokensPerInterval: 25, interval: 1500 });

      isImporting.current = true;

      for (const [i, sheetRow] of Object.entries(importPricingRules)) {
        const itemResp = await getCatalogItems({ customerId: entityId, params: { productNumberContains: i, offset: 0, take: 25 } }).unwrap();

        const item = itemResp.data[0];

        if (!item) {
          console.log('Item not found', i);
          _setErrorItems((prevState) => [...prevState, { productNumber: i, errors: [`Item not found`] }]);
          continue;
        }

        await limiter.removeTokens(1);
        const itemAny = item as any;
        const formattedPricing: any[] = (itemAny.pricing ?? []).map((rule: any) => ({
          ...rule,
          effectiveDate: rule.effectiveDate ? new Date(rule.effectiveDate).toLocaleDateString() : '',
          endDate: rule.endDate ? new Date(rule.endDate).toLocaleDateString() : ''
        }));

        const duplicateRules = sheetRow.filter((newRule) =>
          formattedPricing.some((existingRule: any) => Object.entries(newRule).every(([key, value]) => existingRule[key as keyof PricingRules] === value))
        );

        if (duplicateRules.length > 0) {
          console.log('Rule Exists', duplicateRules);
          const newRules = duplicateRules.map((rule) => ({ ...rule, productNumber: i, errors: [`Pricing rule already exists`] }));

          _setErrorItems((prevState) => [...prevState, ...newRules]);
        }
        const newRules = sheetRow.filter(
          (newRule) => !formattedPricing.some((existingRule: any) => Object.entries(newRule).every(([key, value]) => existingRule[key as keyof PricingRules] === value))
        );

        if (newRules.length > 0) {
          const updatedPricing = [...formattedPricing, ...newRules];

          console.log('Updating Pricing Rules', i, updatedPricing);

          updatePricingRules({ customerId: entityId, productId: item.id, payload: { ...item, pricing: updatedPricing } as any })
            .unwrap()
            .then((product) => {
              console.log('Success', product);
              _setSuccessItems((prevState) => [...prevState, ...(newRules as any)]);
            })
            .catch((error) => {
              const newErrors = newRules.map((rule) => ({ ...rule, productNumber: i, errors: [error.data] }));

              if ('data' in error && error.status === 429) {
                console.log('Hit Rate Limit again');

                _setErrorItems((prevState) => [...prevState, ...newErrors]);
              } else if ('data' in error) {
                _setErrorItems((prevState) => [...prevState, ...newErrors]);
              } else {
                _setErrorItems((prevState) => [...prevState, ...newRules.map((rule) => ({ ...rule, productNumber: i, errors: [error.message] }))]);
              }
            });
        }
      }
    }
  }, [entityId, importPricingRules, isBusinessEdit]);

  /* ****************** Use Effects ****************** */
  useEffect(() => {
    if (!isImporting.current) {
      console.log('Importing Pricing Rules');
      importCatalogItems();
    }
  }, [importCatalogItems, importPricingRules]);

  useEffect(() => {
    if (_successItems.length + _errorItems.length === importTotal) {
      dispatch(setSuccessItems(_successItems));
      message.info('Import finished processing');
    }
  }, [_errorItems.length, _successItems, _successItems.length, dispatch, importTotal]);

  useEffect(() => {
    return (): void => {
      dispatch(setSuccessItems([]));
      dispatch(setErrorItems([]));
      dispatch(setImportItems([]));
    };
  }, [dispatch]);

  /* ****************** Render ****************** */
  return (
    <Stack flexDirection="column">
      <ImportHeader errorItems={_errorItems} />
      <Tabs defaultActiveKey={activeTab} onChange={handleNavigation} type="card">
        <TabPane tab="Success" key="success">
          <PricingTableImport dataSource={_successItems as any} loading={importLoadingConfig} />
        </TabPane>
        <TabPane tab="Errors" key="errors">
          <Table
            size="small"
            dataSource={_errorItems}
            loading={importLoadingConfig}
            columns={[
              {
                dataIndex: 'productNumber',
                title: (
                  <Text ellipsis strong>
                    Product Number
                  </Text>
                ),
                key: 'productNumber',
                width: '30ch'
              },
              {
                dataIndex: 'errors',
                title: (
                  <Text ellipsis strong>
                    Error Message
                  </Text>
                ),
                key: 'errors',
                render: (value: string[]): JSX.Element => (
                  <Stack flexDirection="column">
                    {value.map((item, index) => (
                      <Text key={index}>{item}</Text>
                    ))}
                  </Stack>
                )
              }
            ]}
            rowKey="productNumber"
          />
        </TabPane>
      </Tabs>
    </Stack>
  );
};
